// API client for the task client. Settings (the interim bearer token, the tenant, who
// is working and on which device) live in localStorage until enrolment and shift login
// arrive (R7.22, R7.24). Types mirror the server's responses.

import { getLang, setLang } from "../i18n.js";
import { type TenantConfig, uuidv7, type EventEnvelope, type HandlingProfileConfig, type SiteConfig, type CaptureOverrides, type SupplierLabelFacts } from "@trail/core";

const KEYS = { token: "trail.apiToken", tenant: "trail.tenantId", actor: "trail.actorId", device: "trail.deviceId" } as const;

export type ErpReportKind = "sales-order.picked" | "sales-order.released" | "receipt.received" | "receipt.closed" | "stock.adjusted" | "stock.moved";
export type ErpReportState = "pending" | "claimed" | "shadow" | "sent" | "failed" | "skipped";
export interface ErpReportOut { id: string; kind: ErpReportKind; ref: string; state: ErpReportState; attempts: number; claimedBy: string | null; claimedAt: string | null; result: { mode?: string; operations?: number; notes?: string[]; preview?: string | null; error?: string; erp?: unknown; source?: string } | null; createdBy: string | null; createdAt: string; updatedAt: string; payload?: unknown }
export interface ErpJournalState { floor: string | null; backfillDoneAt: string | null; openingDoneAt: string | null; openingReport: { floor: string; rows: number; openings: number; negative: number; untouched: number; sum: string } | null; appliedCount: number; lastAppliedAt: string | null; pending: number; byState: Record<string, number> }
export interface ErpJournalEntry { key: string; at: string; type: string; sku: string; lot: string | null; rowKey: string | null; stockChange: string; uom: string; warehouse: string | null; otherWarehouse: string | null; orderKind: string | null; orderRef: string | null; partyKey: string | null; invoice: string | null; by: string | null; byIntegration: boolean; corrected: boolean; state: string; note: string | null; matchedReportId: string | null; applied: { containers: string[]; events: { type: string; eventId: string; containerId?: string }[] } | null; appliedAt: string | null }
export interface ErpReportSummary { counts: Record<ErpReportState, number>; latest: ErpReportOut | null; guard: { pending: number; maxPerRun: number; releasedUntil: string | null; held: boolean } }
export type AutomationTrigger = "pallet.started" | "pallet.closed" | "piece.split" | "carton.picked" | "order.picked" | "order.released";
export type AutomationAction =
  | { kind: "print"; document: "sscc-label" | "pallet-label" | "piece-label" | "customer-label" | "delivery-note"; printerRole: string | null; printerId: string | null; copies: number }
  | { kind: "email"; to: string; subject: string; body: string; attach: "none" | "delivery-note" }
  | { kind: "webhook"; url: string; secret: string };
export interface AutomationIn { id?: string; name: string; enabled: boolean; scope: { flow: "receiving" | "picking" | null; customerId: string | null }; trigger: AutomationTrigger; guard: string | null; actions: AutomationAction[] }
export interface AutomationOut extends AutomationIn { id: string; version: number; seeded: string | null; updatedBy: string | null; updatedAt: string; lastRun: { at: string; status: string } | null }
export interface AutomationRunOut { id: string; at: string; status: "ok" | "failed" | "skipped"; guard: boolean | null; outcome: { action: string; ok: boolean; detail: string }[]; automation: string; trigger: AutomationTrigger; data: Record<string, unknown> }
export interface MailStatus { kind: "none" | "smtp" | "graph"; hasPassword: boolean; verifiedAt: string | null; graphReady: boolean }
export type AutomationEventIn =
  | { type: "pallet.started"; flow: "receiving" | "picking" | "move"; sscc: string; orderId?: string }
  | { type: "pallet.closed"; flow: "receiving" | "picking"; containerId: string; orderId?: string; reused?: boolean }
  | { type: "piece.split"; flow: "receiving" | "picking"; piece: NonNullable<PrintRequest["piece"]>; orderId?: string }
  | { type: "carton.picked"; flow: "picking"; containerId: string; orderId: string };

export interface Settings {
  apiToken: string;
  tenantId: string;
  actorId: string;
  deviceId: string;
}

function read(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

export function getSettings(): Settings {
  let deviceId = read(KEYS.device);
  if (!deviceId) {
    deviceId = `dev:${uuidv7().slice(-12)}`;
    write(KEYS.device, deviceId);
  }
  return { apiToken: read(KEYS.token), tenantId: read(KEYS.tenant), actorId: read(KEYS.actor), deviceId };
}

export function saveSettings(s: Partial<Settings>): void {
  if (s.apiToken !== undefined) write(KEYS.token, s.apiToken.trim());
  if (s.tenantId !== undefined) write(KEYS.tenant, s.tenantId.trim());
  if (s.actorId !== undefined) write(KEYS.actor, s.actorId.trim());
}

export function settingsReady(s: Settings = getSettings()): boolean {
  return s.apiToken.length > 0 && /^[0-9a-f-]{36}$/i.test(s.tenantId) && s.actorId.length > 0;
}

/** Apply a setup payload — the `setup` query value of a one-time link, or the whole link as
 *  read from a QR code by the app's own camera (an installed iOS web app has its own storage,
 *  so a link opened in Safari never reaches it). Returns what was set. */
export function applySetupPayload(text: string): { apiToken: boolean; tenantId: boolean } | null {
  let setup = text.trim().replace(/^\]\w\w/, "");
  try {
    const url = new URL(setup);
    setup = url.searchParams.get("setup") ?? "";
  } catch {
    /* not a URL: treat as the raw payload */
  }
  if (!setup) return null;
  try {
    const v = JSON.parse(atob(setup.replace(/-/g, "+").replace(/_/g, "/"))) as { token?: string; tenant?: string };
    saveSettings({ ...(v.token ? { apiToken: v.token } : {}), ...(v.tenant ? { tenantId: v.tenant } : {}) });
    return { apiToken: Boolean(v.token), tenantId: Boolean(v.tenant) };
  } catch {
    return null;
  }
}

/** Device preferences the design language exposes (theme, mounted mode) and the site's
 *  flow preferences until they come from configuration (R7.17, chapter 6). */
export interface Prefs {
  /** "site": the site's default — dark on the task client, light in administration. */
  theme: "site" | "dark" | "light" | "auto";
  mounted: boolean;
  /** Let the on-screen keyboard open in the scan field (false: handheld scanner, keyboard on request). */
  keyboard: boolean;
  /** Scan feedback sound (owner 2026-10-29: the scanner itself is silent): off, normal or loud. */
  sound: "off" | "normal" | "loud";
  /** Characters this device's scanner is configured to send instead of GS (ASCII 29); see docs/devices. */
  gsSubstitutes: string;
  /** Device-level fallback for the receiving location when the site has not set one. */
  receivingLocationId: string | null;
}

const PREFS_KEY = "trail.prefs";
const DEFAULT_PREFS: Prefs = { theme: "site", mounted: false, keyboard: false, sound: "loud", gsSubstitutes: "~|#", receivingLocationId: null };

export function getPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<Prefs>) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(p: Partial<Prefs>): void {
  write(PREFS_KEY, JSON.stringify({ ...getPrefs(), ...p }));
}

/** Theme and mounted mode are attributes on <html> that the stylesheet keys on. */
/** Theme resolution user → device → site (owner 2026-10-28). */
export function effectiveTheme(p: Prefs = getPrefs(), surface: "task" | "admin" = "task"): "dark" | "light" | "auto" {
  const user = getSession()?.theme ?? null;
  if (user) return user;
  return p.theme === "site" ? (surface === "admin" ? "light" : "dark") : p.theme;
}

export function applyPrefs(p: Prefs = getPrefs(), surface: "task" | "admin" = "task"): void {
  const theme = effectiveTheme(p, surface);
  document.documentElement.dataset["theme"] = theme;
  // The installed app's status bar takes its colour from theme-color: match the page background.
  const dark = theme === "dark" || (theme === "auto" && !window.matchMedia("(prefers-color-scheme: light)").matches);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#0f1318" : "#f1f4f7");
  if (p.mounted) document.documentElement.dataset["mounted"] = "1";
  else delete document.documentElement.dataset["mounted"];
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// --- credentials (chapter 10): a user session, an enrolled device, or the integrator token ---
const SESSION_KEY = "trail.session";
const DEVICE_KEY = "trail.device";

export interface SessionInfo {
  token: string;
  expiresAt: string;
  tenantId: string;
  userId: string;
  name: string;
  email: string;
  permissions: string[];
  /** Role names held (any site). */
  roles?: string[];
  deviceId: string | null;
  /** The user's own preferences; theme null = as the device, then the site. */
  theme?: "dark" | "light" | "auto" | null;
  language?: "sv" | "en";
  tenantName?: string | null;
  /** Every tenant this person may work in (more than one → the UI offers a switch). */
  tenants?: TenantChoice[];
}
export interface DeviceInfo {
  deviceToken: string;
  tenantId: string;
  deviceId: string;
  name: string;
  role: string;
}

export function getSession(): SessionInfo | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const s = raw ? (JSON.parse(raw) as SessionInfo) : null;
    return s && new Date(s.expiresAt).getTime() > Date.now() ? s : null;
  } catch {
    return null;
  }
}
export function saveSession(s: SessionInfo | null): void {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}
export function getDevice(): DeviceInfo | null {
  try {
    const raw = localStorage.getItem(DEVICE_KEY);
    return raw ? (JSON.parse(raw) as DeviceInfo) : null;
  } catch {
    return null;
  }
}
export function saveDevice(d: DeviceInfo | null): void {
  try {
    if (d) localStorage.setItem(DEVICE_KEY, JSON.stringify(d));
    else localStorage.removeItem(DEVICE_KEY);
  } catch {
    /* ignore */
  }
}

/** The bearer for API calls: the user's session first, then the device credential (only
 *  good for starting a shift), then the integrator token from the development settings. */
export function bearer(): string {
  return getSession()?.token ?? getDevice()?.deviceToken ?? getSettings().apiToken;
}

/** Tenant and actor for events: from the session when there is one, else the dev settings. */
export function effectiveSettings(): Settings {
  const s = getSettings();
  const session = getSession();
  const device = getDevice();
  return { ...s, tenantId: session?.tenantId ?? device?.tenantId ?? s.tenantId, actorId: session ? `user:${session.userId}` : s.actorId, deviceId: session?.deviceId ?? device?.deviceId ?? s.deviceId };
}

/** Ready to work: a session, or the development settings. */
export function authReady(): boolean {
  return getSession() !== null || (getDevice() === null && settingsReady());
}

/** A document (PDF) from the API, opened in a new tab: fetched with the session's credential, shown as a blob. */
export async function openDocument(path: string): Promise<void> {
  const win = window.open("", "_blank");
  const r = await fetch(path, { headers: { authorization: `Bearer ${bearer()}` } });
  if (!r.ok) { win?.close(); throw new ApiError(r.status, null, `${path}: ${r.status}`); }
  const url = URL.createObjectURL(await r.blob());
  if (win) win.location.href = url; else window.location.href = url;
}

/** A gateway answer while the server restarts for an update (owner 2026-11-02: the phone showed a raw
 *  URL and "502" at receiving): a read is retried a few times, and the message says what it is. */
const GATEWAY = new Set([502, 503, 504]);
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function api<T>(path: string, init: { method?: string; json?: unknown; timeoutMs?: number } = {}): Promise<T> {
  const method = init.method ?? (init.json !== undefined ? "POST" : "GET");
  for (let attempt = 0; ; attempt++) {
    try {
      return await apiOnce<T>(path, { ...init, method });
    } catch (e) {
      if (e instanceof ApiError && GATEWAY.has(e.status) && method === "GET" && attempt < 3) { await sleep(1500 * (attempt + 1)); continue; }
      throw e;
    }
  }
}

async function apiOnce<T>(path: string, init: { method?: string; json?: unknown; timeoutMs?: number } = {}): Promise<T> {
  const s = { ...getSettings(), apiToken: bearer() };
  const ctl = new AbortController();
  const timer = window.setTimeout(() => ctl.abort(), init.timeoutMs ?? 15_000);
  try {
    const r = await fetch(path, {
      method: init.method ?? (init.json !== undefined ? "POST" : "GET"),
      headers: { authorization: `Bearer ${s.apiToken}`, ...(init.json !== undefined ? { "content-type": "application/json" } : {}) },
      body: init.json !== undefined ? JSON.stringify(init.json) : null,
      signal: ctl.signal,
    });
    const text = await r.text();
    const body: unknown = text ? safeJson(text) : null;
    if (!r.ok) {
      if (GATEWAY.has(r.status)) throw new ApiError(r.status, body, getLang() === "en" ? "The server is restarting or unreachable. Try again in a moment." : "Servern startar om eller är inte nåbar. Försök igen om en stund.");
      const msg = (body as { message?: string; error?: string } | null)?.message ?? (body as { error?: string } | null)?.error ?? `${r.status}`;
      throw new ApiError(r.status, body, `${path}: ${msg}`);
    }
    return body as T;
  } finally {
    window.clearTimeout(timer);
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const t = (path: string) => `/api/t/${effectiveSettings().tenantId}${path}`;

// --- mirrored server types -----------------------------------------------------------

export interface ExpectedReceiptSummary {
  id: string;
  source: string;
  reference: string;
  status: string;
  siteId?: string | null;
  expectedAt: string | null;
  supplier: { id: string; name: string } | null;
  lines: number;
  unresolvedLines: number;
  /** The ERP holds the order (someone is editing it there): no receipt starts (owner 2026-11-03). */
  erpLock?: { process: string; by: string | null; since: string | null; seenAt: string } | null;
  /** "purchase" or "return" (a customer's return order, owner 2026-11-03). */
  kind?: string;
  customer?: { id: string; name: string } | null;
  /** The ERP warehouse the head proposes for the lines (returns). */
  erpWarehouse?: string | null;
  /** The office's text lines (returns). */
  notes?: string[];
}

export interface ReturnFacts {
  customerKey: string | null;
  lots: { lineNo: number | null; productId: string | null; lot: string | null; quantity: string; uom: string; at: string; by: string;
    history: { kind: "sold"; to: { partyId: string | null; name: string | null; key: string | null; orderRef: string | null; at: string | null }; sameCustomer: boolean } | { kind: "unknown"; since: string | null } | null }[];
  reports: { id: string; ref: string; state: string; createdAt: string; error: string | null; notes: string[]; lines: { lineNo: number; sku: string | null; createdBy: string | null; received: string; uom: string; lots: string[] }[]; rows: { lot: string; shipmentNo: number }[] }[];
  deviations: { at: string; detail: string; reason: string | null; lot: string | null }[];
}

export interface ExpectedReceiptLine {
  id: string;
  lineNo: number;
  erpWarehouse?: string | null;
  createdBy?: string | null;
  productId: string | null;
  sku: string | null;
  productName: string | null;
  gtin: string | null;
  supplierArticle: string | null;
  description: string | null;
  quantity: string;
  uom: string;
  lot: string | null;
  bestBefore: string | null;
  expiry: string | null;
  sscc: string | null;
  note: string | null;
}

export interface ExpectedReceiptDetail extends ExpectedReceiptSummary {
  lineItems: ExpectedReceiptLine[];
  closing: { differences?: { lineNo: number | null; productId: string | null; expected: Qty | null; received: Qty[]; reason: string }[]; note?: string | null; actorId?: string; closedAt?: string } | null;
  closedAt: string | null;
}

export interface Qty {
  value: string;
  uom: string;
}

export interface ReceiptProgress {
  receiptId: string;
  status: string;
  lines: { lineNo: number; productId: string | null; sku: string | null; productName: string | null; description: string | null; expected: Qty; received: Qty[]; containers: number }[];
  extra: { productId: string | null; gtin: string | null; productName: string | null; received: Qty[]; containers: number }[];
  pallets: number;
  containers: number;
  palletList: { containerId: string; sscc: string | null; children: number; locationCode: string | null; actorId: string; at: string }[];
}

export interface BundleLocation {
  id: string;
  siteId: string;
  code: string;
  kind: string;
  roles: string[];
  path: string;
  glnExtension: string | null;
  storageClass: string | null;
  /** The ERP warehouse a zone belongs to; null inherits. */
  erpWarehouse?: string | null;
  /** pallets | loose | any; null inherits (top default any). */
  holds?: string | null;
  /** null inherits, 0 no limit, n at most n pallets. */
  palletCapacity?: number | null;
  pickSequence?: number | null;
}

export interface BundleGtin {
  gtin: string;
  level: string | null;
  supplierPartyId: string | null;
  quantityFrom: { ai: string; uom: string } | null;
  captureOverrides: CaptureOverrides | null;
  containsQty: string | null;
  displayName: string | null;
}

export interface BundleProduct {
  id: string;
  sku: string;
  name: string;
  storageRequirement?: string | null;
  standardQuantity?: string | null;
  baseUom: string;
  variableMeasure: boolean;
  handlingProfileId: string | null;
  handlingProfileName: string | null;
  category: string | null;
  /** Classification values per dimension key (the company's registry). */
  classifications?: Record<string, string>;
  gtins: BundleGtin[];
  supplierArticles: { supplierPartyId: string; article: string }[];
}

export interface ReferenceBundle {
  generatedAt: string;
  goodsOwner: { id: string; name: string; gs1CompanyPrefix: string | null };
  sites: { id: string; name: string }[];
  locations: BundleLocation[];
  products: BundleProduct[];
  parties: { id: string; name: string; roles: string[] }[];
  /** Identity keys in stock when the bundle was made: the offline collision check. */
  stockKeys?: string[];
  siteConfig: SiteConfig;
  siteId: string | null;
  profiles: { name: string; version: number; config: HandlingProfileConfig }[];
  rules: { dimension?: string; category: string; profileName: string; allowArticleOverride?: boolean }[];
  /** Company settings: profiles in levels (default profile, lock, the dimension that points out profiles). */
  tenantConfig?: TenantConfig;
  /** The site's published layout; null until one is published. */
  layout?: BundleLayoutOut | null;
  /** The ERP journal's floor date: Trail's history starts there. */
  journalFloor?: string | null;
  dimensions?: { key: string; name: string }[];
}

export interface StepBatch {
  flowId: string;
  flowVersion: number;
  stepId: string;
  events: EventEnvelope[];
  effects: { kind: string; payload: Record<string, unknown> }[];
}

export interface Closing {
  differences: { lineNo: number | null; productId: string | null; expected: Qty | null; received: Qty[]; reason: string }[];
  note: string | null;
  actorId: string;
  deviceId: string | null;
}

export interface BundleLayoutOut {
  version: number;
  unitPerMm: number;
  viewBox: [number, number, number, number] | null;
  racks: { id: string; aisleCode: string | null; x: number; y: number; w: number; h: number; bays: number; bayStart: number; levels: number; compartmentsPerBay: number; orientation: string; overrides: Record<string, { levels?: number; compartmentsPerBay?: number; orientation?: string }> }[];
  areas: { id: string; role: string; x: number; y: number; w: number; h: number; label: string | null; locationCode: string | null }[];
  gates: { id: string; x: number; y: number; w: number; h: number; label: string | null }[];
}
export interface LayoutPlan { name: string; kind: "svg" | "image" | "none"; unitPerMm: number; viewBox: [number, number, number, number] | null }
export interface RackElement {
  id: string; kind: "rack"; x: number; y: number; w: number; h: number; rotation: 0 | 90;
  aisleCode: string | null; bays: number; bayStart?: number; levels: number; compartmentsPerBay: number;
  orientation: "short-out" | "long-out"; overrides: Record<string, { compartmentsPerBay?: number; levels?: number; orientation?: "short-out" | "long-out"; beamLength?: number }>;
  beamLength?: number | null; reverse?: boolean; sizeFrom?: "config" | "plan";
  typeId?: string | null; levelHeights?: number[] | null;
  facing: "left" | "right" | "top" | "bottom"; source: "imported" | "drawn"; label?: string;
  height?: number | null;
}
export interface AreaElement { id: string; kind: "area"; x: number; y: number; w: number; h: number; role: "receiving" | "staging" | "packing" | "other"; locationCode: string | null; storageClass?: string | null; label?: string }
export interface GateElement { id: string; kind: "gate"; x: number; y: number; w: number; h: number; label?: string }
export type LayoutElement = RackElement | AreaElement | GateElement;
export interface LayoutOut { id: string; siteId: string; version: number; status: string; plan: LayoutPlan; planSvg: string | null; elements: LayoutElement[]; createdBy: string | null; updatedAt: string; publishedBy: string | null; publishedAt: string | null }
export interface LayoutPublishReport { areaClasses?: { code: string; storageClass: string; changed: boolean }[]; createdAisles?: string[]; racks: number; unbound: number; created: number; existing: number; notDescribed: { aisle: string; codes: string[] }[]; missingAisles: string[] }

export interface CountPeriodItem {
  key: string; sku: string | null; name: string | null; lot: string | null; cartons: number;
  quantity: { value: string; uom: string }[]; locations: { id: string; code: string }[]; unplaced: number; zone: string | null;
  trailCountedAt: string | null; trailAllInPeriod: boolean; trailSomeInPeriod: boolean; erpCountedOn: string | null; receivedOn: string | null;
  status: "done" | "partial" | "left" | "unplaced"; doneBy: "counted" | "received" | null;
}
export interface CountPeriodStatus {
  period: { from: string; to: string } | null;
  periods: { from: string; to: string }[];
  totals: { all: number; done: number; receivedAfter: number; partial: number; left: number; unplaced: number };
  zones: { code: string; all: number; done: number; partial: number }[];
  items: CountPeriodItem[];
}
export interface CountRound {
  id: string;
  siteId: string;
  name: string;
  status: string;
  quantityRule: string | null;
  createdBy: string | null;
  createdAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  note: string | null;
  locations: number;
  counted: number;
  counting: number;
  present: number;
  missing: number;
  found: number;
}
export interface CountRoundLocation {
  locationId: string;
  code: string;
  path: string;
  state: string;
  heldBy: string | null;
  countedAt: string | null;
  countedBy: string | null;
  present: number | null;
  missing: number | null;
  found: number | null;
}
export interface CountDeviation {
  kind: "missing" | "quantity";
  locationId: string;
  locationCode: string;
  containerId: string;
  sku: string | null;
  lot: string | null;
  gtin: string | null;
  trail: { value: string; uom: string };
  counted: { value: string; uom: string } | null;
}
export interface CountRoundDetail extends CountRound {
  locationList: CountRoundLocation[];
  deviations: CountDeviation[];
}
export interface CountLocationInfo {
  location: { id: string; code: string; path: string; kind: string };
  lock: { heldBy: string; since: string; roundId: string | null } | null;
  rounds: { id: string; name: string; state: string }[];
  containers: ContainerSummary[];
}

export interface SyncStatus {
  bundle: string;
  host: string;
  enabled: boolean;
  lastRunAt: string | null;
  status: string | null;
  pending: boolean;
}

export interface DutyOut {
  id: string;
  host: string;
  kind: string;
  bundle: string;
  intervalMinutes: number;
  config: Record<string, unknown>;
  enabled: boolean;
  lastRunAt: string | null;
  lastSummary: { startedAt?: string; finishedAt?: string | null; status?: string; summary?: Record<string, unknown> } | null;
  state: unknown;
  runRequest: { at: string; what?: string } | null;
  updatedBy: string | null;
  updatedAt: string;
}

export interface ApiKeySummary {
  id: string;
  name: string;
  roles: { role: string; siteId: string | null }[];
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt?: string | null;
}

export interface ProfileRuleRow {
  dimension: string;
  category: string;
  profileName: string;
  allowArticleOverride: boolean;
  updatedBy?: string | null;
  updatedAt?: string;
}

export interface ContainerSummary {
  container_id: string;
  kind: string;
  identity_key: string | null;
  product_id: string | null;
  gtin: string | null;
  lot_number: string | null;
  sscc: string | null;
  quantity: string;
  uom: string;
  parent_id: string | null;
  location_id: string | null;
  serial?: string | null;
  /** False once a piece has been cut out of the container (split). */
  intact?: boolean;
  /** received, stored, picked, packed … (the projection's state). */
  state?: string;
  children: number;
  /** When the container entered the ledger (ISO). */
  received_at?: string | null;
  /** A container that left the building: the customer it went to (owner 2026-11-04, returns). */
  shipped_to?: { partyId: string | null; name: string | null; key: string | null; orderRef: string | null; at: string | null } | null;
}

export type PutAwayReason = "nearest-empty" | "nearest-empty-class" | "near-same-product" | "fixed-bin";
export interface PutAwayProposal {
  locationId: string;
  code: string;
  path: string;
  storageClass: string | null;
  reason: PutAwayReason;
}

export interface PutAwayQueueItem {
  containerId: string;
  kind: string;
  sscc: string | null;
  gtin: string | null;
  lotNumber: string | null;
  productId: string | null;
  locationId: string | null;
  locationCode: string | null;
  children: number;
  best_before: string | null;
}

export interface TenantChoice {
  id: string;
  name: string;
  roles: string[];
}
export type LoginStep =
  | { ok: true; mfaRequired: false; chooseTenant?: false; token: string; expiresAt: string }
  | { ok: true; mfaRequired: true; chooseTenant?: false; challenge: string }
  | { ok: true; chooseTenant: true; choice: string; tenants: TenantChoice[] };

export const Auth = {
  config: () => api<{ entra: boolean }>("/api/auth/config"),
  logoutAll: () => api<{ revoked: number }>("/api/auth/logout-all", { method: "POST", json: {} }),
  start: (email: string) => api<{ method: "password" | "entra" | "none"; entra: boolean }>("/api/auth/start", { json: { email } }),
  password: (email: string, password: string) => api<LoginStep>("/api/auth/password", { json: { email, password } }),
  /** The tenant picked when the address is a user in several (owner 2026-10-29). */
  choose: (choice: string, tenantId: string) => api<LoginStep>("/api/auth/choose", { json: { choice, tenantId } }),
  /** Move the signed-in person to another of their tenants; the answer is the new session. */
  switchTenant: (tenantId: string) => api<{ token: string; expiresAt: string }>("/api/auth/switch", { json: { tenantId } }),
  mfa: (challenge: string, code: string) => api<{ token: string; expiresAt: string }>("/api/auth/mfa", { json: { challenge, code } }),
  me: () => api<{ kind: "user"; expiresAt?: string; tenantId: string; tenantName: string | null; tenants: TenantChoice[]; userId: string; email: string; name: string; permissions: string[]; roles: { role: string; siteId: string | null }[]; deviceId: string | null; language: "sv" | "en"; theme: "dark" | "light" | "auto" | null } | { kind: "device" } | { kind: "integrator" }>("/api/auth/me"),
  updateMe: (prefs: { theme?: "dark" | "light" | "auto" | null; language?: "sv" | "en"; currentPassword?: string; newPassword?: string; shortCode?: string | null }) => api<{ theme: "dark" | "light" | "auto" | null; language: "sv" | "en"; account: UserSummary | null }>("/api/auth/me", { method: "PUT", json: prefs }),
  /** Pair a new one-time-code app for the caller (the old secret stops working). */
  myMfa: (tenantId: string, userId: string) => api<{ secret: string; otpauth: string }>(`/api/t/${tenantId}/users/${userId}/mfa`, { json: {} }),
  account: () => api<{ loginMethod: "password" | "entra"; methods: UserSummary["methods"] | null }>("/api/auth/me"),
  logout: () => api<void>("/api/auth/logout", { method: "POST", json: {} }),
  enrol: (code: string) => api<DeviceInfo>("/api/enrol", { json: { code } }),
  shift: (secret: { shortCode?: string; badge?: string }) => api<{ token: string; expiresAt: string; user: { id: string; name: string } }>("/api/auth/device", { json: secret }),
  entraStartUrl: (email: string) => `/api/auth/entra/start?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent(window.location.pathname)}`,
};

/** At startup with a stored session: ask the server, take the renewed expiry (sessions slide), and drop
 *  the session only when the server says it is gone — never on a network error (the server may be
 *  restarting for an update; owner 2026-11-02: being logged in must survive updates). */
export async function refreshSession(): Promise<"kept" | "renewed" | "dropped" | "offline"> {
  const s = getSession();
  if (!s) return "kept";
  try {
    const me = await Auth.me();
    if (me.kind !== "user") { saveSession(null); return "dropped"; }
    const next: SessionInfo = { ...s, expiresAt: me.expiresAt ?? s.expiresAt, tenantId: me.tenantId, userId: me.userId, name: me.name, email: me.email, permissions: me.permissions, roles: (me.roles ?? []).map((r) => r.role), deviceId: me.deviceId, theme: me.theme };
    saveSession(next);
    return next.expiresAt !== s.expiresAt ? "renewed" : "kept";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) { saveSession(null); return "dropped"; }
    return "offline";
  }
}

/** After a login: fetch /me with the new token and store the session. */
export async function adoptSession(token: string, expiresAt: string): Promise<SessionInfo> {
  // (a switch to another tenant comes through here too: the new session replaces the old)
  saveSession({ token, expiresAt, tenantId: "", userId: "", name: "", email: "", permissions: [], deviceId: null });
  const me = await Auth.me();
  if (me.kind !== "user") {
    saveSession(null);
    throw new Error("not a user session");
  }
  const s: SessionInfo = { token, expiresAt, tenantId: me.tenantId, userId: me.userId, name: me.name, email: me.email, permissions: me.permissions, roles: (me.roles ?? []).map((r) => r.role), deviceId: me.deviceId, theme: me.theme, language: me.language, tenantName: me.tenantName, tenants: me.tenants ?? [] };
  saveSession(s);
  // The user's language and theme apply as soon as they are logged in (user → device → site).
  setLang(me.language);
  applyPrefs();
  return s;
}

export interface Overview {
  today: Record<string, number>;
  deviations: { last30Days: number; latest: { sequence: string; at: string; actorId: string; deviceId: string | null; payload: { containerId?: string | null; disposition?: string; detail?: string; gtin?: string | null; lot?: string | null; reason?: string | null; expectedReceiptId?: string | null } }[] };
  putAway: { waiting: number; oldest: PutAwayQueueItem | null };
  receipts: { id: string; reference: string; status: string; expectedAt: string | null }[];
  ledger: { lastSequence: number; lastAt: string | null };
  devices: { id: string; name: string; role: string; enrolled: boolean; lastSeenAt: string | null }[];
}

export interface RoleGrant {
  role: "operator" | "lead" | "quality" | "administrator" | "readonly" | "integrator";
  siteId: string | null;
}
export interface UserSummary {
  id: string;
  email: string;
  name: string;
  roles: RoleGrant[];
  language: string;
  theme: "dark" | "light" | "auto" | null;
  active: boolean;
  loginMethod: "password" | "entra";
  homeSiteId: string | null;
  queues: string[];
  methods: { password: boolean; mfa: boolean; entra: boolean; shortCode: boolean; badge: boolean };
  createdAt: string;
}
export interface SiteOut { id: string; name: string; gln: string | null; address: { street: string; postcode: string; city: string; country: string }; isOwn: boolean; active: boolean; locations: number; zones: number; printers: number; agents: string[]; users: string[]; devices: string[]; editedBy: string | null; editedAt: string | null }
export interface DeviceSummary {
  id: string;
  name: string;
  role: string;
  siteId: string | null;
  queues: string[];
  enrolled: boolean;
  enrolPending: boolean;
  lastSeenAt: string | null;
  active: boolean;
}
export interface ProfileVersion {
  id: string;
  name: string;
  version: number;
  publishedAt: string | null;
  authorId: string | null;
  note: string | null;
  config: HandlingProfileConfig;
  articles: number;
}

export interface Deviation {
  sequence: string;
  at: string;
  actorId: string;
  deviceId: string | null;
  reference: string | null;
  payload: Overview["deviations"]["latest"][number]["payload"];
  handled: { by: string; at: string; note: string | null } | null;
  /** A held cancellation behind this deviation (owner 2026-11-02), when it is one. */
  hold?: { id: string; state: string; count: number; orderKind: string } | null;
  /** An automation the runner gave up on (owner 2026-11-03); retryable while the moment is still unprocessed. */
  automation?: { eventId: string; retryable: boolean } | null;
}
export interface ErpWarehouseOut { source: string; key: string; name: string; firstSeenAt: string; lastSeenAt: string; zones: { id: string; code: string; path: string; siteId: string; siteName: string }[] }
export interface CancelHold { id: string; source: string; orderKind: string; references: string[]; openCount: number; reason: string; runAt: string; state: string; decidedBy: string | null; decidedAt: string | null }
export interface ScopeCheck { open: number; outside: number; min: string | null; max: string | null; missing: number; basis: string }
export interface HistoryEvent {
  sequence: string;
  type: string;
  recordedAt: string;
  occurredAt: string;
  actorId: string;
  deviceId: string | null;
  flowId: string | null;
  flowVersion: number | null;
  capture: string;
  captureDetail: string | null;
  payload: Record<string, unknown>;
}
export interface StockRow {
  container_id: string;
  kind: string;
  sscc: string | null;
  gtin: string | null;
  lot_number: string | null;
  quantity: string;
  uom: string;
  parent_id: string | null;
  state: string;
  intact: boolean;
  location_id: string | null;
  location_code: string | null;
  location_path: string | null;
  sku: string | null;
  product_name: string | null;
  children: number;
  best_before: string | null;
}
export interface LedgerEvent {
  eventId: string;
  sequence: number;
  type: string;
  class: string;
  occurredAt: string;
  recordedAt: string;
  actorId: string;
  deviceId: string | null;
  capture: string;
  captureDetail: string | null;
  flowId: string | null;
  flowVersion: number | null;
  batchId: string;
  hash: string;
  payload: Record<string, unknown>;
}

export interface GtinRegister {
  prefix: string | null;
  capacity: number;
  assigned: { gtin: string; ref: string; productId: string | null; sku: string | null; name: string | null; level: string | null; source: string | null; active: boolean }[];
  reserved: { gtin: string; ref: string; sku: string | null; note: string | null; source: string | null }[];
  /** Distinct item references in use (a GTIN-13 and a GTIN-14 with indicator 9 share one). */
  usedRefs: number;
  free: number;
  nextRef: string | null;
  nextGtin: string | null;
}

export interface SupplierSummary {
  id: string;
  name: string;
  gln: string | null;
  key: string | null;
  shortName: string | null;
  country: string | null;
  articles: number;
  labelFacts: SupplierLabelFacts;
}

/** A print place key; the site names its places (printing.places), three are built in. */
export type PrinterRole = string;

/** Queue a document or label and tell the user what happened (used by the admin's print buttons). */
export async function printAndTell(req: PrintRequest, t: (k: never, v?: Record<string, string | number>) => string): Promise<void> {
  try {
    const job = await Admin.printJob(req);
    window.alert(t("adm.print.queued" as never, { title: job.title, printer: job.printerName }));
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    window.alert(/no_printer|no active/i.test(msg) ? t((req.kind === "delivery-note" || req.kind === "pick-list" || req.kind === "pallet-content" || req.kind === "count-sheet" ? "adm.print.noDocPrinter" : "adm.print.noLabelPrinter") as never) : msg);
  }
}

/** The site the administration works in (System → Anläggningar, stored per browser); null = the server's default. */
export function adminSiteId(): string | null { try { return localStorage.getItem("trail.adminSiteId"); } catch { return null; } }

export type TemplateScope = { siteId?: string | null; customerPartyId?: string | null };
const scopeQs = (s?: TemplateScope) => { const q = new URLSearchParams(); if (s?.siteId) q.set("siteId", s.siteId); if (s?.customerPartyId) q.set("customerPartyId", s.customerPartyId); const str = q.toString(); return str ? `?${str}` : ""; };
export interface TemplateSummary { kind: string; siteId: string | null; customerPartyId: string | null; customerName: string | null; name: string; published: { version: number; publishedAt: string | null; publishedBy: string | null } | null; draft: { version: number; createdAt: string; createdBy: string } | null; isDefault: boolean; isLabel: boolean }
export interface TemplatesIndex { templates: TemplateSummary[]; kinds: { kind: string; name: string; variants: { id: string; name: string }[] }[]; filters: string[]; labelStock: { name: string; width: number; height: number }[]; paperSizes: { name: string; width: number; height: number }[] }
export interface TemplateOut { id: string; kind: string; siteId: string | null; customerPartyId: string | null; name: string; version: number; state: "draft" | "published" | "archived"; body: unknown; note: string | null; createdBy: string; createdAt: string; publishedBy: string | null; publishedAt: string | null }
export interface TemplateFieldInfo { path: string; label: string; type: string; children?: TemplateFieldInfo[] }
export interface TemplateBundle { current: TemplateOut | null; published: TemplateOut | null; draft: TemplateOut | null; default: unknown; versions: Omit<TemplateOut, "body">[]; fields: TemplateFieldInfo[]; sample: unknown }
export type TemplatePreviewSource = { sample: true } | { containerId?: string; orderId?: string; locationId?: string; roundId?: string };
export interface TemplatePreview { pages: unknown[]; zpl: string | null; warnings: { elementId: string | null; message: string }[]; data: unknown }
export interface PrinterOut {
  id: string;
  siteId: string | null;
  name: string;
  model: string | null;
  path: string;
  roles: PrinterRole[];
  active: boolean;
  agentHost: string | null;
  dpi: 203 | 300 | 600;
  kind: "label" | "document";
  lastSeenAt: string | null;
  lastError: string | null;
  lastJob: { at: string; title: string; state: string } | null;
}
export interface PrintJobOut {
  id: string;
  printerId: string;
  printerName: string;
  kind: string;
  format: "zpl" | "pdf";
  templateVersion: number | null;
  subject: string | null;
  title: string;
  copies: number;
  state: string;
  error: string | null;
  requestedBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface FoundPrinter {
  name: string;
  driver?: string | null;
  port?: string | null;
  shared?: boolean;
  shareName?: string | null;
  status?: string | null;
  hostName?: string | null;
}
export interface AgentOut {
  host: string;
  /** Runs on its own credential for this tenant, or still on the server's key. */
  enrolled?: boolean;
  platform: string | null;
  version: string | null;
  lastSeenAt: string;
  found: FoundPrinter[];
  log: { at: string; level: "info" | "error"; message: string }[];
}
export interface AgentTaskOut {
  id: string;
  host: string | null;
  kind: string;
  request: { url: string } | { endpoint: string; identity: string | null; bodyXml: string };
  state: "queued" | "running" | "done" | "failed" | string;
  result: { status: number; contentType: string | null; bytes: number; text: string } | null;
  error: string | null;
  requestedBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface SalesOrderSummary {
  id: string;
  source: string;
  reference: string;
  status: string;
  siteId?: string | null;
  deliveryDate: string | null;
  carrier: string | null;
  pickupAt: string | null;
  minShelfLifeDays: number | null;
  customer: { id: string; name: string } | null;
  lines: number;
  unresolvedLines: number;
  pickedAt: string | null;
  pickedBy: string | null;
  /** The ERP holds the order (someone is editing it there): no pick starts (owner 2026-11-03). */
  erpLock?: { process: string; by: string | null; since: string | null; seenAt: string } | null;
  /** The pick's and the release's report to the ERP (owner 2026-11-03): "failed" = plockad, ej rapporterad. */
  erpReport?: { pickedId: string | null; picked: string | null; released: string | null; error: string | null } | null;
  releasedAt: string | null;
  releasedBy: string | null;
  queue: string | null;
  queueBy: string | null;
}
export interface SalesOrderLine {
  id: string;
  lineNo: number;
  productId: string | null;
  sku: string | null;
  productName: string | null;
  gtin: string | null;
  description: string | null;
  quantity: string;
  uom: string;
  note: string | null;
  /** The customer's label wish for this product (customer-products import), null when none. */
  customerLabel?: { customerArticle: string | null; carton: boolean; pallet: boolean; cartonText: string | null; palletText: string | null } | null;
}
export interface SalesOrderDetail extends SalesOrderSummary {
  external: unknown;
  picking: unknown;
  lineItems: SalesOrderLine[];
}
export interface PickCandidate {
  containerId: string;
  /** "trade-item": a carton with an identity; "loose": a bulk quantity at the place (the quantity is typed). */
  kind: "trade-item" | "loose";
  identityKey: string | null;
  gtin: string | null;
  lotNumber: string | null;
  bestBefore: string | null;
  quantity: string;
  uom: string;
  locationId: string | null;
  code: string | null;
  path: string | null;
  pickSequence: number | null;
  parentId: string | null;
  parentSscc: string | null;
  /** Top-level containers at the location, every product: the zero-point asks when the last leaves. */
  locationRoots: number;
}
export interface PickLineProposal {
  lineNo: number;
  productId: string | null;
  quantity: string;
  uom: string;
  items: PickCandidate[];
  pallet: { containerId: string; sscc: string | null; children: number } | null;
  split: { containerId: string; take: string; remaining: string } | null;
  short: string | null;
}
export interface PickedReport {
  pallets: { containerId: string; sscc: string; items: string[]; sharedWith?: string[] }[];
  lines: { lineNo: number; containerIds: string[]; lots: string[]; quantity: string; short: string | null; reason: string | null }[];
  /** Seconds the server may wait for the ERP's verdict before answering (owner 2026-11-03). */
  wait?: number;
}
export interface PrintRequest {
  kind: "pallet-label" | "pallet-sample" | "sscc-label" | "location-label" | "location-labels" | "test-label" | "piece-label" | "item-label" | "customer-label" | "delivery-note" | "pick-list" | "pallet-content" | "count-sheet";
  containerId?: string;
  /** An item label for goods without a barcode (owner 2026-11-04). */
  item?: { productId: string; quantity?: string | null; lot?: string | null; bestBefore?: string | null };
  orderId?: string;
  roundId?: string;
  piece?: { gtin?: string | null; sku: string; name: string; lot: string; serial?: string | null; parentLot?: string | null; quantity: string; uom: string; reference?: string | null };
  sscc?: string;
  locationId?: string;
  printerId?: string;
  printerRole?: PrinterRole | string;
  copies?: number;
}

export const Admin = {
  /** The fields the ERP leaves on the company's orders (external.<source>.<field>) with one value seen, for the queue rules. */
  orderFields: () => api<{ path: string; sample: string | null }[]>(t("/sales-orders/fields")),
  overview: (siteId?: string | null) => api<Overview>(t(`/overview${siteId ? `?siteId=${encodeURIComponent(siteId)}` : ""}`)),
  printers: () => api<PrinterOut[]>(t("/printers")),
  sites: (inactive = false) => api<SiteOut[]>(t(`/sites${inactive ? "?inactive=1" : ""}`)),
  putSite: (s: { id?: string; name: string; gln: string | null; address: SiteOut["address"]; isOwn: boolean; active: boolean }) => api<SiteOut>(t("/sites"), { method: "PUT", json: s }),
  salesOrders: (status = "open,picking", all = false, siteId?: string | null) => api<SalesOrderSummary[]>(t(`/sales-orders?status=${encodeURIComponent(status)}${all ? "&all=1" : ""}${siteId ? `&siteId=${encodeURIComponent(siteId)}` : ""}`)),
  /** The ERP mirror on demand: ask for a run now, and see when it last ran. */
  syncRefresh: (what?: string, keys?: string[]) => api<{ requested: number; pending: number; fresh: number }>(t("/sync/refresh"), { method: "POST", json: { ...(what ? { what } : {}), ...(keys?.length ? { keys } : {}) } }),
  syncStatus: () => api<SyncStatus[]>(t("/sync/status")),
  salesOrder: (id: string) => api<SalesOrderDetail>(t(`/sales-orders/${id}`)),
  /** Release a picked order: the ERP's finish step, when the company runs pick and release as two steps. */
  releaseOrder: (id: string) => api<{ ok: true }>(t(`/sales-orders/${id}/released`), { method: "POST", json: {} }),
  unpickOrder: (id: string, reason: string) => api<{ ok: true; containers: number }>(t(`/sales-orders/${id}/unpick`), { method: "POST", json: { reason } }),
  retryReport: (id: string) => api<{ ok: true }>(t(`/erp-reports/${id}/retry`), { method: "POST", json: {} }),
  shipOrder: (id: string) => api<{ ok: true; containers: number; pallets: number }>(t(`/sales-orders/${id}/shipped`), { method: "POST", json: {} }),
  deliveryNote: (id: string, draft = false) => openDocument(t(`/sales-orders/${id}/delivery-note.pdf${draft ? "?draft=1" : ""}`)),
  printAgents: () => api<AgentOut[]>(t("/print-agents")),
  agentTasks: () => api<AgentTaskOut[]>(t("/agent-tasks")),
  /** A one-time enrolment code for the site agent on a host (10 minutes); a known host is re-enrolled. */
  /** Duties: the company's standing orders to its agent (the ERP mirror on a schedule). */
  duties: () => api<DutyOut[]>(t("/agent-duties")),
  /** What Trail owes the ERP (owner 2026-11-01): the report log, and a retry that sends again. */
  erpReports: (f: { state?: ErpReportState; kind?: ErpReportKind; q?: string; since?: string; limit?: number; offset?: number } = {}) => api<{ items: ErpReportOut[]; total: number }>(t(`/erp-reports?${new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]))).toString()}`)),
  erpReportSummary: () => api<ErpReportSummary>(t("/erp-reports/summary")),
  /** The ERP's stock journal (owner 2026-11-04): where the mirror stands, the entries, the opening position. */
  erpJournal: (source?: string) => api<ErpJournalState>(t(`/erp/journal${source ? `?source=${encodeURIComponent(source)}` : ""}`)),
  erpJournalEntries: (f: { source?: string; state?: string; type?: string; sku?: string; order?: string; from?: string; to?: string; limit?: number } = {}) => api<{ items: ErpJournalEntry[] }>(t(`/erp/journal/entries?${new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]))).toString()}`)),
  erpJournalOpening: (source?: string) => api<{ floor: string; rows: number; openings: number; negative: number; untouched: number; sum: string }>(t("/erp/journal/opening"), { method: "POST", json: { source } }),
  erpJournalApply: (source?: string) => api<{ applied: number; ignored: number; waiting: number; deviations: number; remaining: number; errors: { key: string; message: string }[] }>(t("/erp/journal/apply"), { method: "POST", json: { source, limit: 2000 } }),
  erpReport: (id: string) => api<ErpReportOut>(t(`/erp-reports/${id}`)),
  retryErpReport: (id: string) => api<{ ok: true }>(t(`/erp-reports/${id}/retry`), { method: "POST", json: {} }),
  dismissErpReport: (id: string) => api<{ ok: true }>(t(`/erp-reports/${id}/dismiss`), { method: "POST", json: {} }),
  releaseErpReports: () => api<{ until: string }>(t("/erp-reports/release"), { method: "POST", json: {} }),
  putDuty: (d: { host: string; bundle: string; intervalMinutes: number; config?: Record<string, unknown>; enabled: boolean }) => api<DutyOut>(t("/agent-duties"), { method: "PUT", json: d }),
  deleteDuty: (id: string) => api<void>(t(`/agent-duties/${id}`), { method: "DELETE" }),
  bundles: () => api<string[]>(t("/agent-bundles")),
  bundleConfig: (name: string) => api<{ blocks: string[]; defaults: Record<string, unknown> }>(t(`/agent-bundles/${encodeURIComponent(name)}/config`)),
  enrolAgent: (host: string) => api<{ host: string; enrolCode: string; expiresAt: string; reenrol: boolean }>(t("/print-agents/enrol"), { json: { host } }),
  createAgentTask: (input: { host: string | null; kind: "fetch"; url: string }) => api<{ id: string }>(t("/agent-tasks"), { method: "POST", json: input }),
  deletePrinter: (id: string) => api<{ jobs: number }>(t(`/printers/${id}`), { method: "DELETE" }),
  putPrinter: (p: { id?: string; name: string; model: string | null; path: string; roles: PrinterRole[]; active: boolean; agentHost: string | null; dpi: 203 | 300 | 600; kind: "label" | "document" }) => api<PrinterOut>(t("/printers"), { method: "PUT", json: p }),
  // Label and document templates (docs/design/templates.md).
  templates: () => api<TemplatesIndex>(t("/templates")),
  template: (kind: string, scope?: TemplateScope) => api<TemplateBundle>(t(`/templates/${kind}${scopeQs(scope)}`)),
  saveTemplateDraft: (kind: string, body: unknown, name: string | undefined, note: string | null | undefined, scope?: TemplateScope) => api<TemplateOut>(t(`/templates/${kind}/draft${scopeQs(scope)}`), { method: "PUT", json: { body, ...(name ? { name } : {}), ...(note !== undefined ? { note } : {}) } }),
  discardTemplateDraft: (kind: string, scope?: TemplateScope) => api<{ removed: boolean }>(t(`/templates/${kind}/draft${scopeQs(scope)}`), { method: "DELETE" }),
  publishTemplate: (kind: string, scope?: TemplateScope) => api<TemplateOut>(t(`/templates/${kind}/publish${scopeQs(scope)}`), { method: "POST" }),
  revertTemplate: (kind: string, version: number, scope?: TemplateScope) => api<TemplateOut>(t(`/templates/${kind}/revert/${version}${scopeQs(scope)}`), { method: "POST" }),
  resetTemplate: (kind: string, variant?: string, scope?: TemplateScope) => api<TemplateOut>(t(`/templates/${kind}/reset${scopeQs(scope)}`), { method: "POST", json: variant ? { variant } : {} }),
  deleteTemplateScope: (kind: string, scope: TemplateScope) => api<{ removed: number }>(t(`/templates/${kind}${scopeQs(scope)}`), { method: "DELETE" }),
  previewTemplate: (kind: string, body: unknown, source: TemplatePreviewSource, dpi?: number) => api<TemplatePreview>(t(`/templates/${kind}/preview`), { method: "POST", json: { body, source, ...(dpi ? { dpi } : {}) }, timeoutMs: 30000 }),
  testPrintTemplate: (kind: string, body: unknown, printerId: string, source: TemplatePreviewSource) => api<{ id: string; title: string }>(t(`/templates/${kind}/test-print`), { method: "POST", json: { body, printerId, source }, timeoutMs: 30000 }),
  printDocument: (req: { kind: string; containerId?: string; orderId?: string; roundId?: string; locationId?: string; printerId?: string; printerRole?: PrinterRole; copies?: number }) => api<PrintJobOut>(t("/print-jobs"), { method: "POST", json: req }),
  printJobPdfUrl: (jobId: string) => t(`/print-jobs/${jobId}/pdf`),
  printJobs: (limit = 100) => api<PrintJobOut[]>(t(`/print-jobs?limit=${limit}`)),
  printJob: (r: PrintRequest) => api<PrintJobOut>(t("/print-jobs"), { json: r }),
  /** A business moment for the automations (spec §6.8); the answer says how many prints it queues. */
  automationEvent: (e: AutomationEventIn) => api<{ id: string; prints: number }>(t("/automation-events"), { json: e }),
  automations: () => api<{ automations: AutomationOut[]; triggers: AutomationTrigger[]; mail: MailStatus }>(t("/automations")),
  saveAutomation: (a: AutomationIn) => api<{ id: string }>(t("/automations"), { method: "PUT", json: a }),
  rehearseAutomation: (a: AutomationIn, days = 7) => api<{ total: number; fires: number; rows: { at: string; what: string; inScope: boolean; guard: boolean | null; fires: boolean; actions: string[] }[] }>(t("/automations/rehearse"), { method: "POST", json: { automation: a, days } }),
  deleteAutomation: (id: string) => api<{ ok: true }>(t(`/automations/${id}`), { method: "DELETE" }),
  checkGuard: (guard: string) => api<{ ok: boolean; message?: string }>(t("/automations/check-guard"), { json: { guard } }),
  automationRuns: () => api<{ runs: AutomationRunOut[] }>(t("/automation-runs")),
  mailStatus: () => api<MailStatus>(t("/mail")),
  mailPassword: (password: string) => api<{ ok: true }>(t("/mail/password"), { method: "PUT", json: { password } }),
  mailTest: (to: string) => api<MailStatus>(t("/mail/test"), { json: { to } }),
  printJobZpl: (id: string) => api<{ id: string; title: string; zpl: string }>(t(`/print-jobs/${id}/zpl`)),
  suppliers: () => api<SupplierSummary[]>(t("/suppliers")),
  putSupplierLabelFacts: (partyId: string, facts: SupplierLabelFacts) => api<SupplierLabelFacts>(t(`/suppliers/${partyId}/label-facts`), { method: "PUT", json: facts }),
  gtinRegister: () => api<GtinRegister>(t("/gtin-register")),
  /** The goods owner's GS1 company prefix (owner 2026-10-29: settable from GTIN-register, not only by script). */
  setGs1Prefix: (gs1CompanyPrefix: string | null) => api<{ goodsOwnerId: string }>(t("/goods-owner/gs1-prefix"), { method: "PUT", json: { gs1CompanyPrefix } }),
  allocateGtin: (productId: string, level: "each" | "inner" | "case" | "pallet" | null, indicator = 0) => api<{ gtin: string; ref: string }>(t("/gtin-register/allocate"), { json: { productId, level, indicator } }),
  createLocation: (l: { parentId: string | null; code: string; kind: string; roles?: string[]; storageClass?: string | null; erpWarehouse?: string | null; holds?: string | null; palletCapacity?: number | null; pickSequence?: number | null }) => api<{ id: string; path: string }>(t("/locations"), { json: l }),
  updateLocation: (id: string, patch: { code?: string; kind?: string; roles?: string[]; storageClass?: string | null; erpWarehouse?: string | null; holds?: string | null; palletCapacity?: number | null; pickSequence?: number | null; active?: boolean; parentId?: string | null }) => api<{ id: string; path: string; moved: number }>(t(`/locations/${id}`), { method: "PATCH", json: patch }),
  inactiveLocations: () => api<{ id: string; code: string; path: string; kind: string; editedBy: string | null; editedAt: string | null }[]>(t("/locations/inactive")),
  locationHistory: (locationId: string) => api<{ sequence: string; type: string; recordedAt: string; actorId: string; deviceId: string | null; payload: Record<string, unknown> }[]>(t(`/locations/${locationId}/history`)),
  deviations: (handled: "false" | "true" | "all" = "false") => api<Deviation[]>(t(`/deviations?handled=${handled}`)),
  handleDeviation: (sequence: string, note: string | null) => api<{ sequence: number }>(t(`/deviations/${sequence}/handle`), { json: { note } }),
  retryAutomation: (sequence: string) => api<{ sequence: number; retried: boolean }>(t(`/deviations/${sequence}/retry-automation`), { method: "POST", json: {} }),
  erpWarehouses: () => api<ErpWarehouseOut[]>(t("/erp/warehouses")),
  cancelHolds: (state?: string) => api<CancelHold[]>(t(`/erp/cancel-holds${state ? `?state=${state}` : ""}`)),
  decideCancelHold: (id: string, decision: "apply" | "dismiss") => api<{ state: string; cancelled?: number }>(t(`/erp/cancel-holds/${id}/${decision}`), { method: "POST", json: {} }),
  scopeCheck: (q: { orderKind: "sales-order" | "expected-receipt"; source?: string; column: "orderDate" | "deliveryDate"; from: string | null }) => api<ScopeCheck>(t(`/erp/scope-check?orderKind=${q.orderKind}${q.source ? `&source=${encodeURIComponent(q.source)}` : ""}&column=${q.column}${q.from ? `&from=${q.from}` : ""}`)),
  receipts: (status = "open,proposed,closed", siteId?: string | null, kind?: "purchase" | "return") => api<ExpectedReceiptSummary[]>(t(`/expected-receipts?status=${encodeURIComponent(status)}${siteId ? `&siteId=${encodeURIComponent(siteId)}` : ""}${kind ? `&kind=${kind}` : ""}`)),
  /** A return's page (owner 2026-11-04): batch history per received lot, reports to the ERP, deviations. */
  returnFacts: (id: string) => api<ReturnFacts>(t(`/expected-receipts/${id}/return-facts`)),
  history: (containerId: string) => api<HistoryEvent[]>(t(`/containers/${containerId}/history`)),
  epcis: (after = 0, limit = 5000) => api<Record<string, unknown>>(t(`/epcis/events?after=${after}&limit=${limit}`)),
  epcisForContainer: (containerId: string) => api<Record<string, unknown>>(t(`/epcis/containers/${containerId}/events`)),
  container: (containerId: string) => api<ContainerSummary & { lot_number: string | null; location_id: string | null; children: ContainerSummary[] }>(t(`/containers/${containerId}`)),
  stock: (productId?: string) => api<StockRow[]>(t(`/stock${productId ? `?productId=${productId}` : ""}`)),
  events: (after = 0, limit = 200, latest = false) => api<LedgerEvent[]>(t(`/events?after=${after}&limit=${limit}${latest ? "&latest=1" : ""}`)),
  integrity: () => api<{ ok: boolean; eventsChecked: number; tamperedEvents: unknown[]; gaps: unknown[] }>(t("/integrity")),
  users: () => api<UserSummary[]>(t("/users")),
  /** Integration keys: a token per integration with roles, shown once at creation. */
  apiKeys: () => api<ApiKeySummary[]>(t("/api-keys")),
  createApiKey: (name: string, roles: { role: string; siteId: string | null }[], expiresInDays?: number | null) => api<{ key: ApiKeySummary; token: string }>(t("/api-keys"), { json: { name, roles, expiresInDays: expiresInDays ?? null } }),
  revokeApiKey: (id: string) => api<void>(t(`/api-keys/${id}`), { method: "DELETE" }),
  putUser: (u: { email: string; name: string; roles: RoleGrant[]; language?: string; homeSiteId?: string | null; queues?: string[]; password?: string | null; shortCode?: string | null; badge?: string | null; active?: boolean; loginMethod?: "password" | "entra" }) => api<UserSummary>(t("/users"), { method: "PUT", json: u }),
  enrolMfa: (userId: string) => api<{ secret: string; otpauth: string }>(t(`/users/${userId}/mfa`), { json: {} }),
  devices: () => api<DeviceSummary[]>(t("/devices")),
  reissueDevice: (deviceId: string) => api<{ enrolCode: string; expiresAt: string; enrolUrl: string }>(t(`/devices/${deviceId}/enrol-code`), { method: "POST", json: {} }),
  removeDevice: (deviceId: string) => api<{ result: "deleted" | "deactivated" }>(t(`/devices/${deviceId}`), { method: "DELETE" }),
  patchDevice: (id: string, patch: { siteId?: string | null; queues?: string[] }) => api<{ ok: true }>(t(`/devices/${id}`), { method: "PATCH", json: patch }),
  setOrderQueue: (id: string, queue: string | null) => api<{ ok: true }>(t(`/sales-orders/${id}/queue`), { json: { queue } }),
  requeue: () => api<{ changed: number }>(t("/sales-orders/requeue"), { method: "POST" }),
  createDevice: (d: { name: string; role: "handheld" | "mounted" | "workstation"; siteId?: string | null; queues?: string[] }) => api<{ device: DeviceSummary; enrolCode: string; expiresAt: string; enrolUrl: string }>(t("/devices"), { json: d }),
  siteConfig: (siteId: string) => api<{ siteId: string; config: SiteConfig; version: number; updatedBy: string | null; updatedAt: string | null }>(t(`/sites/${siteId}/config`)),
  putSiteConfig: (siteId: string, config: SiteConfig) => api<{ siteId: string; config: SiteConfig; version: number }>(t(`/sites/${siteId}/config`), { method: "PUT", json: config }),
  profiles: () => api<{ name: string; current: ProfileVersion | null; versions: ProfileVersion[] }[]>(t("/profiles")),
  publishProfile: (name: string, config: unknown, note: string | null) => api<ProfileVersion>(t(`/profiles/${encodeURIComponent(name)}/versions`), { json: { config, note } }),
  rules: () => api<ProfileRuleRow[]>(t("/profile-rules")),
  /** Replace the rules of one dimension (default "category"); other dimensions' rules stay. */
  putRules: (rules: { category: string; profileName: string; allowArticleOverride?: boolean }[], dimension = "category") => api<ProfileRuleRow[]>(t("/profile-rules"), { method: "PUT", json: { dimension, rules } }),
  /** Company settings (profiles in levels) and the registry of classification dimensions. */
  tenantConfig: () => api<{ config: TenantConfig; version: number; updatedBy: string | null; updatedAt: string | null }>(t("/config")),
  putTenantConfig: (config: TenantConfig) => api<{ config: TenantConfig; version: number }>(t("/config"), { method: "PUT", json: config }),
  dimensions: () => api<{ key: string; name: string; source: string | null; values: number; products: number }[]>(t("/dimensions")),
  renameDimension: (key: string, name: string) => api<{ key: string; name: string }>(t(`/dimensions/${encodeURIComponent(key)}`), { method: "PUT", json: { name } }),
  /** The article level: a profile set on the article itself; null clears it. */
  setProductStorage: (productId: string, storageRequirement: string | null) => api<{ productId: string; storageRequirement: string | null }>(t(`/products/${productId}/storage`), { method: "PUT", json: { storageRequirement } }),
  setProductProfile: (productId: string, profileName: string | null) => api<{ productId: string; profileName: string | null }>(t(`/products/${productId}/profile`), { method: "PUT", json: { profileName } }),
};

export const Api = {
  printJob: (r: PrintRequest) => api<PrintJobOut>(t("/print-jobs"), { json: r }),
  /** A business moment for the automations (spec §6.8); the answer says how many prints it queues. */
  automationEvent: (e: AutomationEventIn) => api<{ id: string; prints: number }>(t("/automation-events"), { json: e }),
  automations: () => api<{ automations: AutomationOut[]; triggers: AutomationTrigger[]; mail: MailStatus }>(t("/automations")),
  saveAutomation: (a: AutomationIn) => api<{ id: string }>(t("/automations"), { method: "PUT", json: a }),
  deleteAutomation: (id: string) => api<{ ok: true }>(t(`/automations/${id}`), { method: "DELETE" }),
  checkGuard: (guard: string) => api<{ ok: boolean; message?: string }>(t("/automations/check-guard"), { json: { guard } }),
  automationRuns: () => api<{ runs: AutomationRunOut[] }>(t("/automation-runs")),
  mailStatus: () => api<MailStatus>(t("/mail")),
  mailPassword: (password: string) => api<{ ok: true }>(t("/mail/password"), { method: "PUT", json: { password } }),
  mailTest: (to: string) => api<MailStatus>(t("/mail/test"), { json: { to } }),
  container: (containerId: string) => api<ContainerSummary & { children: ContainerSummary[] }>(t(`/containers/${containerId}`)),
  locationContents: (locationId: string) => api<ContainerSummary[]>(t(`/locations/${locationId}/contents`)),
  /** Warehouse layout (mockup admin-v1/layout). */
  layout: (withSvg = true) => { const s = adminSiteId(); const qs = [withSvg ? "" : "svg=0", s ? `siteId=${encodeURIComponent(s)}` : ""].filter(Boolean).join("&"); return api<{ draft: LayoutOut | null; published: LayoutOut | null }>(t(`/layout${qs ? `?${qs}` : ""}`)); },
  saveLayout: (input: { siteId?: string; plan: LayoutPlan; planSvg?: string | null | undefined; elements: LayoutElement[] }) => api<LayoutOut>(t("/layout"), { method: "PUT", json: { ...(adminSiteId() ? { siteId: adminSiteId() } : {}), ...input } }),
  publishLayout: (dryRun: boolean) => api<LayoutPublishReport>(t("/layout/publish"), { method: "POST", json: { dryRun, ...(adminSiteId() ? { siteId: adminSiteId() } : {}) } }),
  /** Stock counting (mockup counting-v1). */
  countRounds: (status = "open,review") => api<CountRound[]>(t(`/count-rounds?status=${encodeURIComponent(status)}`)),
  countRound: (id: string) => api<CountRoundDetail>(t(`/count-rounds/${id}`)),
  createCountRound: (r: { name: string; locationIds: string[]; quantityRule?: "none" | "split" | "all" | null; note?: string | null }) => api<CountRound>(t("/count-rounds"), { json: r }),
  approveCountRound: (id: string) => api<{ adjusted: number }>(t(`/count-rounds/${id}/approve`), { method: "POST", json: {} }),
  cancelCountRound: (id: string) => api<void>(t(`/count-rounds/${id}/cancel`), { method: "POST", json: {} }),
  countLocation: (locationId: string) => api<CountLocationInfo>(t(`/count/locations/${locationId}`)),
  claimCountLocation: (locationId: string, roundId: string | null) => api<{ heldBy: string; since: string }>(t(`/count/locations/${locationId}/claim`), { method: "POST", json: { roundId } }),
  releaseCountLocation: (locationId: string) => api<void>(t(`/count/locations/${locationId}/release`), { method: "POST", json: {} }),
  /** "No, something is still here" at the zero-point: the location goes into today's zero-point round. */
  zeroPointCount: (locationId: string) => api<{ roundId: string; name: string }>(t("/count/zero-point"), { json: { locationId } }),
  countLast: () => api<{ locationId: string; at: string; by: string }[]>(t("/count/last")),
  /** The count period: every batch in stock against the site's newest period, or the one named. */
  countPeriod: (period?: { from: string; to: string } | null) => { const s = adminSiteId(); const qs = [s ? `siteId=${encodeURIComponent(s)}` : "", period ? `from=${period.from}&to=${period.to}` : ""].filter(Boolean).join("&"); return api<CountPeriodStatus>(t(`/count/period${qs ? `?${qs}` : ""}`)); },
  countLocks: () => api<{ locationId: string; code: string; heldBy: string; since: string; roundId: string | null }[]>(t("/count/locks")),
  containersByIdentity: (key: string, timeoutMs?: number) => api<ContainerSummary[]>(t(`/containers/by-identity?key=${encodeURIComponent(key)}`), timeoutMs ? { timeoutMs } : {}),
  putAwayQueue: () => api<PutAwayQueueItem[]>(t("/put-away/queue")),
  salesOrders: (status = "open,picking", all = false, siteId?: string | null) => api<SalesOrderSummary[]>(t(`/sales-orders?status=${encodeURIComponent(status)}${all ? "&all=1" : ""}${siteId ? `&siteId=${encodeURIComponent(siteId)}` : ""}`)),
  /** The ERP mirror on demand: ask for a run now, and see when it last ran. */
  syncRefresh: (what?: string, keys?: string[]) => api<{ requested: number; pending: number; fresh: number }>(t("/sync/refresh"), { method: "POST", json: { ...(what ? { what } : {}), ...(keys?.length ? { keys } : {}) } }),
  syncStatus: () => api<SyncStatus[]>(t("/sync/status")),
  stock: (productId?: string) => api<StockRow[]>(t(`/stock${productId ? `?productId=${productId}` : ""}`)),
  salesOrder: (id: string) => api<SalesOrderDetail>(t(`/sales-orders/${id}`)),
  releaseOrder: (id: string) => api<{ ok: true }>(t(`/sales-orders/${id}/released`), { method: "POST", json: {} }),
  unpickOrder: (id: string, reason: string) => api<{ ok: true; containers: number }>(t(`/sales-orders/${id}/unpick`), { method: "POST", json: { reason } }),
  shipOrder: (id: string) => api<{ ok: true; containers: number; pallets: number }>(t(`/sales-orders/${id}/shipped`), { method: "POST", json: {} }),
  deliveryNote: (id: string, draft = false) => openDocument(t(`/sales-orders/${id}/delivery-note.pdf${draft ? "?draft=1" : ""}`)),
  pickProposal: (id: string) => api<{ orderId: string; reference: string; lines: PickLineProposal[] }>(t(`/sales-orders/${id}/pick-proposal`)),
  startPicking: (id: string) => api<{ ok: true }>(t(`/sales-orders/${id}/picking`), { json: {} }),
  /** Back out of a pick that recorded no pallet: the order is open again. */
  releasePicking: (id: string) => api<{ ok: true }>(t(`/sales-orders/${id}/release`), { json: {} }),
  reportPicked: (id: string, report: PickedReport) => api<{ ok: true; verdict?: "sent" | "unknown" }>(t(`/sales-orders/${id}/picked`), { json: report }),
  putAwayPropose: (containerId: string) => api<{ proposal: PutAwayProposal | null }>(t("/put-away/propose"), { json: { containerId } }),
  listReceipts: (kind?: "purchase" | "return") => api<ExpectedReceiptSummary[]>(t(`/expected-receipts${kind ? `?kind=${kind}` : ""}`)),
  /** A line created at the door of a return (owner 2026-11-03): goods the order had no line for, with the chosen warehouse. */
  addReceiptLine: (id: string, input: { productId: string; erpWarehouse: string | null }) => api<{ lineNo: number; created: boolean }>(t(`/expected-receipts/${id}/lines`), { json: input }),
  getReceipt: (id: string) => api<ExpectedReceiptDetail>(t(`/expected-receipts/${id}`)),
  progress: (id: string) => api<ReceiptProgress>(t(`/expected-receipts/${id}/progress`)),
  close: (id: string, closing: Closing) => api<{ status: string; closedAt: string; alreadyClosed: boolean }>(t(`/expected-receipts/${id}/close`), { json: closing }),
  bundle: () => { let site: string | null = null; try { site = localStorage.getItem("trail.adminSiteId"); } catch { site = null; } return api<ReferenceBundle>(t(`/reference${site ? `?siteId=${encodeURIComponent(site)}` : ""}`), { timeoutMs: 60_000 }); },
  issueSscc: () => api<{ sscc: string; serial: number }>(t("/identifiers/sscc"), { json: {} }),
  /** The site's layout without the plan drawing, for the phone's 3D "Gå till" (published is what counts). */
  layoutPublished: () => api<{ draft: LayoutOut | null; published: LayoutOut | null }>(t("/layout?svg=0"), { timeoutMs: 30_000 }),
  postStep: (batch: StepBatch) => api<{ duplicate: boolean }>(t("/steps"), { json: batch }),
};
